<template>
<main class="content">
  <div class="container mb-3">
    <InfoBox class="mb-3" />
    <div class="row">
      <div class="col-md-8">
        <h3>Individueller Zaun-Konfigurator</h3>
        <small>mit Sofortpreis fur komplette Zaunanlagen.</small>
        <p><span class="badge badge-danger live">Live Vorschau</span></p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-9">
        <LivePreview />
        <div class="row mt-3">
          <div class="col-3">
            <a href="#!" @click="printed" class="link-icon mt-4"><i class="fas fa-print"></i> Angebot drucken</a>
          </div>
          <div class="col-9">
            <Carousel
              v-if="systemReady && !exampleImagesLoading"
              style-class="carousel slide configurator__carousel"
              :key="carouselKey"
            />
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <ControlBox />
      </div>
    </div>
  </div>
  <StepNavigation />
  <Options />
  <Construction :option-one="optionsPfosten" :loading="cLoading"/>
  <div class="bg-light">
    <div class="container">
      <ContentDescription class="dynamic-content" :description="description" />
    </div>
  </div>
  <CarouselModal />
  <RequestForm />
  <PopupModal />
</main>
</template>
<script>
import StepNavigation from './common/StepNavigation'
import Options from './common/Options'
import LivePreview from './common/LivePreview.vue'
import Construction from '@/pages/system/common/Construction.vue'
import Carousel from '@/pages/system/common/carousel.vue'
import CarouselModal from '@/pages/system/common/carouselModal.vue'
import ControlBox from '@/pages/system/common/ControlBox.vue'
import InfoBox from '~/pages/configurator/common/infoBox'
import RequestForm from '~/pages/configurator/common/requestForm'
import PopupModal from '~/components/Popup'
import ContentDescription from '~/pages/configurator/common/contentDescription'
import axios from 'axios'
import { Base64 } from 'js-base64'
import { onMounted } from '@vue/runtime-core'
import { useStore } from 'vuex'
import { useMeta } from 'vue-meta'
import { computed } from '@vue/reactivity'

export default {
  components: {
    ControlBox,
    InfoBox,
    Construction,
    StepNavigation,
    Options,
    LivePreview,
    Carousel,
    CarouselModal,
    RequestForm,
    PopupModal,
    ContentDescription
  },
  setup () {
    const store = useStore()

    const pageTitle = computed(() => store.getters['system/pageTitle'])
    const metaDescription = computed(() => store.getters['system/metaDescription'])
    const description = computed(() => store.getters['system/description'])

    useMeta(computed(() => ({
      title: pageTitle.value || 'Loewe-Zaun.de - System',
      meta: [
        {
          name: 'description',
          content: metaDescription.value || '',
          vmid: 'description'
        }
      ]
    })))

    const loading = computed(() => store.getters['system/loading'])
    const exampleImagesLoading = computed(() => store.getters['system/exampleImagesLoading'])
    const selectedModelId = computed(() => {
      const system = store.getters['system/system']
      const step = system?.base?.steps?.find(item => item.model_step)
      return step?.options?.find(option => option.selected)?.id || ''
    })
    const exampleImagesSignature = computed(() => {
      const images = store.getters['system/exampleImages'] || []
      return images.map(image => image.path).join('|')
    })
    const carouselKey = computed(() => {
      return `${selectedModelId.value}|${exampleImagesSignature.value}`
    })
    const systemReady = computed(() => {
      return !!store.getters['system/system'] && !store.getters['system/previewImagesLoading']
    })
    const cLoading = computed(() => store.getters['system/cLoading'])
    const optionsPfosten = computed(() => {
      const pfosten = store.getters['system/getOptionsPfosten']
      if (typeof pfosten !== 'undefined') {
        const option = pfosten.options.find(s => s.selected)
        if (option.name.toLowerCase() === 'eigene pfosten') {
          return false
        }
      }
      return true
    })

    async function initSystem () {
      const apiKey = btoa(process.env.VUE_APP_API_KEY)
      const apiUrl = process.env.VUE_APP_API_URL
      await axios.post(apiUrl + 'builder', { re: apiKey }).then(resp => {
        if (resp.data && resp.data.definition) {
          store.dispatch('system/setSystem', resp.data.definition)
        }
      })
    }

    async function printed () {
      const apiKey = btoa(process.env.VUE_APP_API_KEY)
      const apiUrl = process.env.VUE_APP_API_URL
      const p = document.getElementById('print-content')
      if (!p) {
        return
      }

      const summary = store.getters['system/summary'] || {}
      const productItems = summary.products || []
      let html = ''

      const toPrintPrice = (value) => {
        const price = Number(value)
        return Number.isFinite(price) ? price : 0
      }

      const normalizePrintImages = (images) => {
        return (images || [])
          .map((image, index) => {
            if (typeof image === 'string') {
              return { path: image, order: index }
            }
            if (image && typeof image.path === 'string' && image.path) {
              return {
                path: image.path,
                order: typeof image.order === 'number' ? image.order : index
              }
            }
            return null
          })
          .filter(Boolean)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      }

      const resolvePrintImages = (item, configKey) => {
        const previewGroups = store.getters['system/previewImages'] || []
        const fromPreview = normalizePrintImages(
          previewGroups.find(group => group.config === configKey)?.images
        )
        if (fromPreview.length) {
          return fromPreview
        }
        const system = store.getters['system/system']
        if (configKey && system?.[configKey]?.getPreviewImages) {
          const fromConfig = normalizePrintImages(system[configKey].getPreviewImages())
          if (fromConfig.length) {
            return fromConfig
          }
        }
        return normalizePrintImages(item.images)
      }

      const buildPrintPayload = (item, configKey = null) => ({
        id: item.id,
        name: item.name,
        images: resolvePrintImages(item, configKey),
        price: {
          discounted: toPrintPrice(item.price?.discounted),
          total: toPrintPrice(item.price?.total),
          old_price: toPrintPrice(item.price?.total ?? item.price?.old_price)
        },
        steps: (item.steps || []).map(step => ({
          name: step.name,
          value: step.value ?? step.selectedLabel ?? '--',
          price: toPrintPrice(step.price),
          print_description: step.printDescription || step.print_description || null
        }))
      })

      try {
        if (productItems.length > 0) {
          for (let i = 0; i < productItems.length; i++) {
            const product = productItems[i]
            const payload = buildPrintPayload(product.summary || {}, product.config || null)
            const config = Base64.encode(JSON.stringify(payload))
            const resp = await axios.post(apiUrl + 'print/configurator', {
              re: apiKey,
              config,
              include_header: i === 0,
              include_footer: i === productItems.length - 1
            })
            if (resp.data) {
              html += resp.data
            }
          }
        } else {
          const totals = store.getters['system/getTotalPrice'] || {}
          const payload = {
            id: store.getters['system/system']?.id,
            name: 'Individueller Zaun-Konfigurator',
            images: [],
            price: {
              discounted: toPrintPrice(totals.discounted),
              total: toPrintPrice(totals.price),
              old_price: toPrintPrice(totals.price)
            },
            steps: (store.getters['system/steps'] || [])
              .filter(step => step.type !== 'total')
              .map(step => ({
                name: step.name,
                value: step.selectedLabel || '--',
                price: 0,
                print_description: null
              }))
          }
          const config = Base64.encode(JSON.stringify(payload))
          const resp = await axios.post(apiUrl + 'print/configurator', {
            re: apiKey,
            config,
            include_header: true,
            include_footer: true
          })
          html = resp.data || ''
        }

        if (html) {
          p.innerHTML = html
          setTimeout(() => {
            window.print()
          }, 500)
        }
      } catch (error) {
        console.error(error)
      }
    }

    onMounted(() => {
      initSystem()
    })

    return {
      loading,
      systemReady,
      exampleImagesLoading,
      cLoading,
      optionsPfosten,
      carouselKey,
      description,
      printed
    }
  }
}
</script>
